import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StarRating } from '../components/StarRating';
import { getReviews, createReview } from '../lib/api';
import { toast } from 'sonner';
import { MessageSquare, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const data = await getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createReview(formData);
      toast.success('¡Gracias por tu reseña! Será revisada y publicada pronto.');
      setDialogOpen(false);
      setFormData({ patient_name: '', patient_email: '', rating: 5, comment: '' });
    } catch (error) {
      toast.error('Error al enviar la reseña');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Cargando reseñas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="reviews-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
            Reseñas de Pacientes
          </h1>
          <p className="text-muted-foreground text-lg mb-6">
            Conoce las experiencias de quienes nos han visitado
          </p>
          
          {reviews.length > 0 && (
            <div className="inline-flex items-center gap-3 bg-white border border-border rounded-full px-6 py-3">
              <span className="text-3xl font-bold text-foreground">{averageRating}</span>
              <div>
                <StarRating rating={Math.round(Number(averageRating))} />
                <p className="text-sm text-muted-foreground">{reviews.length} reseñas</p>
              </div>
            </div>
          )}
        </div>

        {/* Write Review Button */}
        <div className="flex justify-center mb-8">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2" data-testid="write-review-btn">
                <MessageSquare className="w-4 h-4" />
                Escribir una Reseña
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Comparte tu Experiencia</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
                <div>
                  <Label htmlFor="patient_name">Tu nombre *</Label>
                  <Input
                    id="patient_name"
                    value={formData.patient_name}
                    onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                    required
                    placeholder="Nombre completo"
                    className="h-11 mt-1"
                    data-testid="review-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="patient_email">Tu email *</Label>
                  <Input
                    id="patient_email"
                    type="email"
                    value={formData.patient_email}
                    onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                    required
                    placeholder="tu@email.com"
                    className="h-11 mt-1"
                    data-testid="review-input-email"
                  />
                </div>
                <div>
                  <Label>Calificación *</Label>
                  <div className="mt-2">
                    <StarRating
                      rating={formData.rating}
                      interactive
                      size="lg"
                      onChange={(rating) => setFormData({ ...formData, rating })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="comment">Tu comentario *</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    required
                    placeholder="Cuéntanos tu experiencia..."
                    rows={4}
                    className="mt-1"
                    data-testid="review-input-comment"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full gap-2"
                  disabled={submitting}
                  data-testid="submit-review-btn"
                >
                  {submitting ? 'Enviando...' : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Reseña
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reviews Grid */}
        {reviews.length === 0 ? (
          <Card className="border border-border p-12 text-center">
            <CardContent className="p-0">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Aún no hay reseñas. ¡Sé el primero en compartir tu experiencia!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-grid">
            {reviews.map((review) => (
              <Card key={review.id} className="border border-border" data-testid={`review-${review.id}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{review.patient_name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {new Date(review.created_at).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                  <p className="text-foreground leading-relaxed">"{review.comment}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
